import { Injectable } from "@angular/core";
import { WindowRef } from "@obg/ngx.global.evo";
import { defer, fromEvent, merge, noop, Observable, of } from "rxjs";
import { map, startWith } from "rxjs/operators";

import { DeviceOrientation } from "./browser-events.model";

export interface BrowserEvents {
	readonly networkStatus$: Observable<boolean>;
	readonly visibility$: Observable<boolean>;
	readonly deviceOrientation$: Observable<DeviceOrientation>;
	readonly userActivity$: Observable<string>;
	dispatchResizeEvent(): void;
	scrollToTop(position: number): void;
}

/**
 * @deprecated -[fabric-deprecated] - Use BrowserEventsService from svc-platform instead.
 * */
@Injectable()
export class BrowserEventsService implements BrowserEvents {
	/**
	 * @deprecated [fabric-deprecated] - Use PlatformServiceValue.NetworkStatus instead.
	 */
	readonly networkStatus$: Observable<boolean> = merge(
		fromEvent(this.windowRef.native, "online").pipe(map(() => true)),
		fromEvent(this.windowRef.native, "offline").pipe(map(() => false)),
		of(this.windowRef.native.navigator.onLine)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use PlatformServiceValue.Visibility instead.
	 */
	readonly visibility$: Observable<boolean> = fromEvent<Event | undefined>(
		this.windowRef.native.document,
		"visibilitychange"
	).pipe(
		startWith(undefined),
		map(() => !this.windowRef.native.document.hidden)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use PlatformServiceValue.DeviceOrientation instead.
	 */
	readonly deviceOrientation$: Observable<DeviceOrientation> = fromEvent<Event | undefined>(
		this.windowRef.native,
		"orientationchange"
	).pipe(
		startWith(undefined),
		map(() => {
			const orientation = this.windowRef.native.orientation;
			return orientation === 90 || orientation === -90 ? DeviceOrientation.Landscape : DeviceOrientation.Portrait;
		})
	);

	/**
	 * @deprecated [fabric-deprecated] - Use PlatformServiceValue.UserActivity instead.
	 */
	readonly userActivity$: Observable<string> = defer(() => {
		const eventsToObserve = ["keydown", "mousedown", "mousemove", "touchstart", "wheel"];

		return merge(...eventsToObserve.map((eventType) => fromEvent(this.windowRef.native, eventType))).pipe(
			map((e) => e.type),
			startWith("")
		);
	});

	constructor(private windowRef: WindowRef) {}

	/**
	 * @deprecated [fabric-deprecated] - Use PlatformServiceCommand.Resize instead.
	 */
	dispatchResizeEvent() {
		this.windowRef.native.dispatchEvent(new Event("resize"));
	}

	/**
	 * @deprecated [fabric-deprecated] - Use PlatformServiceCommand.ScrollToTop instead.
	 */
	scrollToTop(position: number, behavior: ScrollBehavior = "smooth") {
		this.windowRef.native.scrollTo({ top: position, behavior });
	}
}

@Injectable()
export class NoopBrowserEventsService implements BrowserEvents {
	readonly networkStatus$ = of(true);
	readonly visibility$ = of(true);
	readonly deviceOrientation$ = of(DeviceOrientation.Portrait);
	readonly userActivity$ = of("");
	dispatchResizeEvent = noop;
	scrollToTop = noop;
}
